import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useAtom as useStateSegment,
  useSetAtom as useStateSegmentSetter,
  useAtomValue as useStateSegmentValue,
} from 'jotai';
import {
  Sidebar,
  AppLayout,
  HomeIcon,
  GenerateIcon,
  AgenticPlannerIcon,
  UploadIcon,
  ContentRuleCenterIcon,
  KnowledgeCenterIcon,
  ImageGenerationIcon,
  type SidebarNavItem,
} from '@pfizer/shared/components';
import { PAGE_ROUTES, ROLE } from '../../../constants/common';
import MainLayout from '../MainLayout';
import { formatDate, getInitials } from '../../../utils/commonUtils';
import { fetchVersionInfo } from '../../../services/Content';
import { knowledgeCenterModalAtom } from '../../../store/markettingContentGeneration/stateSegments/knowledgeCenterModalStateSegment';
import { knowledgeCenterStateSegment } from '../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import { contentParamsSegment } from '../../../store/markettingContentGeneration/stateSegments/paramsSegment';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { trackRuleCenterInitiate } from '../../../services/Analytics';
import { getEncodedUserNtid } from '../../../store/markettingContentGeneration/helpers/common';
import ModalComponent, { ModalVariant } from '../../molecules/ModalComponent';
import ContentRuleCenterConnected from '../../widgets/ContentRuleCenter/ContentRuleCenterConnected';
import ContentRuleCenterHeader from '../../widgets/ContentRuleCenter/ContentRuleCenterHeader';
import ContentQualityCheck from '../../widgets/ContentQualityCheck';
import KnowledgeCenter from '../../widgets/KnowledgeCenter';
import KnowledgeCenterIconAtom from '../../atoms/KnowledgeCenterIcon';
import styles from '../MainLayout/MainLayout.module.css';
import { GenerateContentValidationStateSegment } from '../../../store/markettingContentGeneration/stateSegments/GenerateContentValidationStateSegment';
import { contentListStateSegment } from '../../../store/markettingContentGeneration/stateSegments/contentListSegment';
import { contentPaginationStateSegment } from '../../../store/markettingContentGeneration/stateSegments/contentPaginationStateSegment';
import { activeEmailScreenStateSegment } from '../../../store/markettingContentGeneration/stateSegments/emailModulesStateSegment';
import { openPopoverStateSegment } from '../../../store/markettingContentGeneration/stateSegments/popoverStateSegment';
import get from 'lodash/get';
import { useAgenticNavigationGuard } from '../../../hooks/useAgenticNavigationGuard';
import { useImageGenerationNavigationGuard } from '../../../hooks/useImageGenerationNavigationGuard';
import { useApiRetryErrorModal } from '../../../hooks/useApiRetryErrorModal';
import Modal from '../../molecules/Modal';

/**
 * RootLayout - Application shell with shared sidebar
 *
 * Provides the main layout structure with:
 * - Shared Sidebar component for navigation
 * - Main content area via React Router Outlet
 * - Modal handling for Knowledge Center and Content Rule Center
 */
export default function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Agentic planner navigation guard
  const { handleNavigationWithGuard, renderNavigationModal } =
    useAgenticNavigationGuard();

  // Image generation navigation guard
  const {
    handleImageGenerationNavigation,
    handleNavigationAwayFromImageGeneration,
    renderImageGenerationModal,
  } = useImageGenerationNavigationGuard();

  // API retry error modal
  const {
    isOpen: isApiRetryErrorModalOpen,
    title: apiRetryErrorTitle,
    description: apiRetryErrorDescription,
    handleContinue: handleApiRetryErrorContinue,
    handleCancel: handleApiRetryErrorCancel,
  } = useApiRetryErrorModal();

  // User info
  const userName =
    sessionStorage.getItem('pfz-cggenai-user-givenname') || 'User';
  const isAdminUser =
    sessionStorage.getItem('pfz-cggenai-user-role') === ROLE.ADMIN;
  const userInitials = getInitials(userName);

  // Feature flags
  const { isContentRuleBookEnabled, isImageGenerationEnabled } =
    useFeatureFlags();

  // Version info for profile menu
  const [versionName, setVersionName] = useState('');
  const [versionDate, setVersionDate] = useState('');

  useEffect(() => {
    (async () => {
      const versionRes = await fetchVersionInfo();
      const versionNameToDisplay =
        versionRes?.applicationConfig?.releaseInfo?.releaseName || '';
      const versionDateToDisplay =
        versionRes?.applicationConfig?.releaseInfo?.releaseDate || '';
      setVersionName(versionNameToDisplay);
      setVersionDate(versionDateToDisplay);
    })();
  }, []);

  // Knowledge Center modal state
  const [knowledgeCenterModalState, setKnowledgeCenterModalState] =
    useStateSegment(knowledgeCenterModalAtom);
  const { isKnowledgeCenterModalOpen } = knowledgeCenterModalState;
  const brandsInformationStateSegmentSetter = useStateSegmentSetter(
    knowledgeCenterStateSegment
  );
  const contentParams = useStateSegmentValue(contentParamsSegment);

  // Content Rule Center / Quality Check modal state
  const [isContentRuleCenterModalOpen, setIsContentRuleCenterModalOpen] =
    useState(false);
  const [isQualityCheckModalOpen, setIsQualityCheckModalOpen] = useState(false);
  const [qcModalSize, setQcModalSize] = useState(ModalVariant.Medium);

  // Content validation state for quality check
  const activeEmailScreen = useStateSegmentValue(activeEmailScreenStateSegment);
  const { contentList: contentItems } = useStateSegmentValue(
    contentListStateSegment
  );
  const currentViewVersion = useStateSegmentValue(
    contentPaginationStateSegment
  );
  const generateContentValidation = useStateSegmentValue(
    GenerateContentValidationStateSegment
  );
  const openPopOver = useStateSegmentSetter(openPopoverStateSegment);

  const { currentSelectedLanguage } = contentParams;
  const contentId = get(
    contentItems[currentViewVersion],
    `${currentSelectedLanguage}.id`,
    ''
  );
  const currentContentValidation = generateContentValidation[contentId];
  const currentContentHasError =
    currentContentValidation &&
    currentContentValidation.isLoading === false &&
    currentContentValidation.error;

  // Check if on generate content route
  const isOnGenerateContentRoute =
    location.pathname === PAGE_ROUTES.GENERATE_CONTENT;

  // Determine active nav item based on current route and modal state
  const activeItemId = useMemo(() => {
    // Modal states take priority for active highlighting
    if (isKnowledgeCenterModalOpen) return 'knowledge-center';
    if (isContentRuleCenterModalOpen || isQualityCheckModalOpen)
      return 'content-rules';

    const pathname = location.pathname;
    if (pathname.startsWith(PAGE_ROUTES.AGENTIC_PLANNER))
      return 'agentic-planner';
    if (pathname.startsWith(PAGE_ROUTES.IMAGE_GENERATION))
      return 'image-generation';
    if (pathname.startsWith(PAGE_ROUTES.UPLOAD_GENERATE)) return 'upload';
    if (pathname.startsWith(PAGE_ROUTES.GENERATE_CONTENT)) return 'create';
    if (pathname.startsWith(PAGE_ROUTES.HOME)) return 'home';
    return 'home';
  }, [
    location.pathname,
    isKnowledgeCenterModalOpen,
    isContentRuleCenterModalOpen,
    isQualityCheckModalOpen,
  ]);

  /**
   * Performs actual navigation after closing any open modals.
   * Called directly or after navigation guard approval.
   */
  const performNavigation = useCallback(
    (href: string) => {
      // Close any open modals when navigating
      if (isKnowledgeCenterModalOpen) {
        setKnowledgeCenterModalState(prev => ({
          ...prev,
          isKnowledgeCenterModalOpen: false,
        }));
      }
      if (isContentRuleCenterModalOpen) setIsContentRuleCenterModalOpen(false);
      if (isQualityCheckModalOpen) setIsQualityCheckModalOpen(false);

      navigate(href);
    },
    [
      navigate,
      isKnowledgeCenterModalOpen,
      isContentRuleCenterModalOpen,
      isQualityCheckModalOpen,
      setKnowledgeCenterModalState,
    ]
  );

  /**
   * Navigation handler with guards for agentic planner and image generation routes.
   * Shows confirmation modal if user has an active session.
   */
  const handleNavigation = useCallback(
    (href: string) => {
      // First check if navigating away from image generation with active session
      const interceptedByImageGen = handleNavigationAwayFromImageGeneration(
        href,
        performNavigation
      );
      if (interceptedByImageGen) {
        return;
      }

      // Then check agentic planner navigation guard
      handleNavigationWithGuard(href, performNavigation);
    },
    [
      handleNavigationAwayFromImageGeneration,
      handleNavigationWithGuard,
      performNavigation,
    ]
  );

  // Knowledge Center click handler
  const handleKnowledgeCenterClick = useCallback(() => {
    // Close quality validation modals if open
    if (isQualityCheckModalOpen || isContentRuleCenterModalOpen) {
      setIsQualityCheckModalOpen(false);
      setIsContentRuleCenterModalOpen(false);
    }

    // Update brands information state with current content params
    brandsInformationStateSegmentSetter(prev => ({
      ...prev,
      apiPayloadUsed: {
        brand: contentParams.currentSelectedBrand,
        country: contentParams.currentSelectedCountry,
        indication: contentParams.currentSelectedIndication
          ? [contentParams.currentSelectedIndication]
          : [],
        targetAudience: [contentParams.currentSelectedAudience],
        language: contentParams.currentSelectedLanguage,
        topics: Array.isArray(contentParams.currentSelectedTopic)
          ? contentParams.currentSelectedTopic
          : [contentParams.currentSelectedTopic],
      },
    }));

    // Toggle knowledge center modal
    setKnowledgeCenterModalState(prev => ({
      ...prev,
      isKnowledgeCenterModalOpen: !isKnowledgeCenterModalOpen,
    }));
  }, [
    isQualityCheckModalOpen,
    isContentRuleCenterModalOpen,
    isKnowledgeCenterModalOpen,
    brandsInformationStateSegmentSetter,
    contentParams,
    setKnowledgeCenterModalState,
  ]);

  // Content Rules / Quality Validation click handler
  const handleContentRulesClick = useCallback(() => {
    // Close knowledge center modal if open
    if (isKnowledgeCenterModalOpen) {
      setKnowledgeCenterModalState(prev => ({
        ...prev,
        isKnowledgeCenterModalOpen: false,
      }));
    }

    if (currentContentHasError) {
      // If content has error, open popover instead of modal
      setIsQualityCheckModalOpen(false);
      setIsContentRuleCenterModalOpen(false);
      openPopOver();
    } else if (isContentRuleBookEnabled) {
      // Track rule center opening
      if (!isContentRuleCenterModalOpen) {
        trackRuleCenterInitiate(getEncodedUserNtid());
      }
      setIsContentRuleCenterModalOpen(!isContentRuleCenterModalOpen);
      setIsQualityCheckModalOpen(false);
    } else {
      // Legacy quality check modal
      setIsQualityCheckModalOpen(!isQualityCheckModalOpen);
      setIsContentRuleCenterModalOpen(false);
    }
  }, [
    isKnowledgeCenterModalOpen,
    isContentRuleBookEnabled,
    isContentRuleCenterModalOpen,
    isQualityCheckModalOpen,
    currentContentHasError,
    setKnowledgeCenterModalState,
    openPopOver,
  ]);

  // Modal close handlers
  const closeContentRuleCenterModal = useCallback(() => {
    setIsContentRuleCenterModalOpen(false);
  }, []);

  const closeQualityChecksModal = useCallback(() => {
    setIsQualityCheckModalOpen(false);
  }, []);

  const handleQualityCheckModalSizeChange = useCallback(
    (size: ModalVariant) => {
      setQcModalSize(size);
    },
    []
  );

  // Top navigation items (main features)
  // Order: Home, Create, Agentic Planner (admin only), Upload
  // const handleAgenticNavigation = () => {
  //   window.location.href = '/agentic/index.html';
  // };

  const topNavItems = useMemo((): SidebarNavItem[] => {
    const items: SidebarNavItem[] = [
      {
        id: 'home',
        icon: <HomeIcon size={24} />,
        label: 'Home',
        onClick: () => handleNavigation(PAGE_ROUTES.HOME),
      },
      {
        id: 'create',
        icon: <GenerateIcon size={24} />,
        label: 'Create',
        onClick: () => handleNavigation(PAGE_ROUTES.GENERATE_CONTENT),
      },
    ];

    // Add agentic planner for admin users (after Create, before Upload)
    if (isAdminUser) {
      items.push({
        id: 'agentic-planner',
        icon: <AgenticPlannerIcon size={24} />,
        label: 'Create New with Agentic AI',
        badge: 'BETA',
        onClick: () => handleNavigation(PAGE_ROUTES.AGENTIC_PLANNER),
        // onClick: () => handleAgenticNavigation(),
      });
    }

    // Image Generation - available when feature flag is enabled
    // Uses navigation guard to show "Start New Session" modal if session exists
    if (isImageGenerationEnabled) {
      items.push({
        id: 'image-generation',
        icon: <ImageGenerationIcon size={24} />,
        label: 'Image Generation',
        badge: 'BETA',
        onClick: () => handleImageGenerationNavigation(performNavigation),
      });
    }

    // Upload comes after Image Generation
    items.push({
      id: 'upload',
      icon: <UploadIcon size={24} />,
      label: 'Upload',
      onClick: () => handleNavigation(PAGE_ROUTES.UPLOAD_GENERATE),
    });

    return items;
  }, [
    isAdminUser,
    isImageGenerationEnabled,
    handleNavigation,
    handleImageGenerationNavigation,
    performNavigation,
  ]);

  // Bottom navigation items (secondary features - modals)
  const bottomNavItems = useMemo((): SidebarNavItem[] => {
    return [
      {
        id: 'content-rules',
        icon: <ContentRuleCenterIcon size={24} />,
        label: isContentRuleBookEnabled ? 'Rules Center' : 'Quality Check',
        onClick: handleContentRulesClick,
      },
      {
        id: 'knowledge-center',
        icon: <KnowledgeCenterIcon size={24} />,
        label: 'Knowledge Center',
        onClick: handleKnowledgeCenterClick,
      },
    ];
  }, [
    isContentRuleBookEnabled,
    handleContentRulesClick,
    handleKnowledgeCenterClick,
  ]);

  // Fixed sidebar width for modal offset
  const sidebarWidth = 80;

  return (
    <AppLayout
      sidebar={
        <Sidebar
          topNavItems={topNavItems}
          bottomNavItems={bottomNavItems}
          userInitials={userInitials}
          userName={userName}
          versionName={versionName}
          versionDate={formatDate(versionDate)}
          activeItemId={activeItemId}
          showIntercom={true}
        />
      }
    >
      <MainLayout>
        <Outlet />
      </MainLayout>

      {/* Content Rule Center Modal (Feature Flag enabled) */}
      <ModalComponent
        isOpen={isContentRuleCenterModalOpen}
        onClose={closeContentRuleCenterModal}
        leftOffset={sidebarWidth}
        variant={ModalVariant.Large}
        title={<ContentRuleCenterHeader />}
      >
        <ContentRuleCenterConnected />
      </ModalComponent>

      {/* Legacy Quality Check Modal (Feature Flag disabled) */}
      <ModalComponent
        isOpen={isQualityCheckModalOpen}
        onClose={closeQualityChecksModal}
        leftOffset={sidebarWidth}
        variant={qcModalSize}
      >
        <ContentQualityCheck
          contentId={contentId}
          isEmailPreviewMode={activeEmailScreen === 'modules-preview'}
          isOnGenerateContentRoute={isOnGenerateContentRoute}
          onSizeChange={handleQualityCheckModalSizeChange}
          onClose={closeQualityChecksModal}
        />
      </ModalComponent>

      {/* Knowledge Center Modal */}
      {isKnowledgeCenterModalOpen && (
        <ModalComponent
          className={styles.knowledgeCenterModal}
          isOpen={isKnowledgeCenterModalOpen}
          onClose={() => {
            setKnowledgeCenterModalState(prev => ({
              ...prev,
              isKnowledgeCenterModalOpen: false,
            }));
          }}
          title='Knowledge Center'
          headerIcon={<KnowledgeCenterIconAtom />}
          leftOffset={sidebarWidth}
          headerStyle={styles.knowledgeCenterModal__header}
        >
          <KnowledgeCenter />
        </ModalComponent>
      )}

      {/* Agentic Navigation Guard Modal */}
      {renderNavigationModal()}

      {/* Image Generation Navigation Guard Modal */}
      {renderImageGenerationModal()}

      {/* API Retry Error Modal - shown when embedded 401 errors exhaust max retries */}
      {isApiRetryErrorModalOpen && (
        <Modal
          title={apiRetryErrorTitle}
          description={apiRetryErrorDescription}
          primaryCTALabel='Continue'
          secondaryCTALabel='Cancel'
          onPrimaryCTAClick={handleApiRetryErrorContinue}
          onSecondaryCTAClick={handleApiRetryErrorCancel}
          isShowPrimaryButton={true}
          isShowSecondaryButton={true}
          showCloseIcon={false}
          isOpen={isApiRetryErrorModalOpen}
        />
      )}
    </AppLayout>
  );
}
